/* eslint-disable */
import React, { FC, useCallback, useContext } from 'react';
import block from 'propmods';
import i18n from '@yandex-int/i18n';
import { Button, Spin, TextInputProps, ControlProps } from 'lego-on-react';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import { Select } from '../Select/Select';
import { ActionButton } from '../ActionButton/ActionButton';
import { getBaseTooltipReducer } from '../../reducers';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import * as keyset from 'i18n/FeatureFlags';
import * as commonKeyset from 'i18n/Common';
import { TextInputEnhanced } from '../TextInputEnhanced/TextInputEnhanced';

import './FeatureFlagsPage.scss';

const b = block('FeatureFlagsPage');

const t = i18n(keyset);
const tCommon = i18n(commonKeyset);

const SkillFlagInput: FC<
    Readonly<
        TextInputProps &
            ControlProps & { index: number; onInputChange: (flagIndex: number, updatedFlag: string | null) => void }
    >
> = (props) => {
    const { index, onInputChange } = props;
    const onChange = useCallback(
        (text: string) => {
            onInputChange(index, text);
        },
        [index, onInputChange],
    );
    return <TextInputEnhanced {...props} escapeSpecialChars onChange={onChange} />;
};

const UserFlagKeyInput: FC<
    Readonly<
        TextInputProps &
            ControlProps & {
                index: number;
                flagValue: boolean;
                onUpdateUserFlag: (flagIndex: number, updatedFlagKey: string | null, updatedFlagValue: boolean) => void;
            }
    >
> = (props) => {
    const { index, flagValue, onUpdateUserFlag } = props;
    const onChange = useCallback(
        (text: string) => {
            onUpdateUserFlag(index, text, flagValue);
        },
        [index, flagValue, onUpdateUserFlag],
    );
    return <TextInputEnhanced {...props} escapeSpecialChars onChange={onChange} />;
};

export const FeatureFlagsPage: React.FC = () => {
    const { isLoading, skillFlags, addSkillFlag, updateSkillFlag, userFlags, addUserFlag, updateUserFlag, saveFlags } =
        useFeatureFlags();

    return (
        <div {...b()}>
            <div>
                {isLoading && (
                    <div {...b('spin-container')}>
                        <Spin size="l" progress />
                    </div>
                )}
                <h2 {...b('title')}> {t('title-skill-feature-flags')} </h2>
                {skillFlags.map((flag, index) => (
                    <div key={String(index)} {...b('row', { adaptive: true })}>
                        <SkillFlagInput
                            text={flag}
                            size="s"
                            theme="normal"
                            index={index}
                            onInputChange={updateSkillFlag}
                        />
                        <div>
                            <Button onClick={() => updateSkillFlag(index, null)} size="s" theme="normal">
                                {tCommon('button-delete')}
                            </Button>
                        </div>
                    </div>
                ))}
                <div {...b('row')}>
                    <Button onClick={addSkillFlag} size="s" theme="normal">
                        {tCommon('add')}
                    </Button>
                </div>

                <h2 {...b('title')}> {t('title-user-feature-flags')} </h2>
                {userFlags.map(({ key, value }, index) => (
                    <div key={index} {...b('row', { adaptive: true })}>
                        <UserFlagKeyInput
                            theme="normal"
                            size="s"
                            text={key}
                            index={index}
                            flagValue={value}
                            onUpdateUserFlag={updateUserFlag}
                        />
                        <div {...b('buttons-block')}>
                            <div>
                                <Select
                                    label={'_val'}
                                    namespace="skill_access"
                                    placeholder={'_val'}
                                    type="radio"
                                    value={value ? 'True' : 'False'}
                                    onChange={(updatedValue) => {
                                        updateUserFlag(index, key, updatedValue === 'True');
                                    }}
                                    options={[
                                        { value: 'True', text: 'True' },
                                        { value: 'False', text: 'False' },
                                    ]}
                                    {...b('boolean-select')}
                                />
                            </div>
                            <Button onClick={() => updateUserFlag(index, null, value)} size="s" theme="normal">
                                {tCommon('button-delete')}
                            </Button>
                        </div>
                    </div>
                ))}
                <div {...b('row')}>
                    <Button onClick={addUserFlag} size="s" theme="normal">
                        {tCommon('add')}
                    </Button>
                </div>
                <ActionButton
                    tooltipReducer={getBaseTooltipReducer({ successLabel: tCommon('saved') })}
                    defaultTooltipState={{ tooltipVisible: false }}
                    action={saveFlags}
                >
                    {t('save-flags')}
                </ActionButton>
            </div>
        </div>
    );
};
